import React from 'react'
import { Col, ConfigProvider, Layout, Row } from 'antd'
import { gutter } from './lib/layout'
import * as Sentry from '@sentry/react'
import styles from './less/BaseLayout.module.less'
import AccessDeniedComponent from './components/Admin/AccessDeniedComponent'
import { ErrorContent } from './ErrorBoundary'
import { useAppThemeConfig, useLogo } from './lib/themeController'

function isChunkLoadError(error) {
  const msg = error.message ?? ''
  // Chrome / Firefox
  if (msg.startsWith('Failed to fetch dynamically imported module')) return true
  // Safari / WebKit — generic "Load failed" for network-level fetch failures
  if (msg === 'Load failed') return true
  // Safari / WebKit — module-loader path. Thrown when the response was fetched
  // successfully but isn't a valid module (typical after deploy: Firebase
  // Hosting SPA rewrite serves index.html for a missing chunk). CL-WEBAPP-A.
  if (msg.startsWith('Importing a module script failed')) return true
  // Safari / WebKit — a stale or failed dynamic import can RESOLVE the import()
  // promise with `undefined` instead of rejecting. React's lazy initializer then
  // crashes reading `_result.default`. Same post-deploy stale-chunk family as
  // above, different surface (minified message e.g. "undefined is not an object
  // (evaluating 'H._result.default')"). CL-WEBAPP-ME.
  if (msg.includes('_result.default')) return true
  // Chrome / V8 — identical crash, but its message names only the property being
  // read, not the full expression, so the `_result` hint above is absent.
  // CL-WEBAPP-MF.
  if (msg === "Cannot read properties of undefined (reading 'default')")
    return true
  return false
}

// firebase-js-sdk @firebase/firestore occasionally throws this during
// listener registration when two snapshot subscriptions race on the same
// query target. A full page reload clears the SDK's in-memory listener
// state and resolves it. Reported as CL-1296 / Sentry CL-WEBAPP-J4 and
// upstream as firebase/firebase-js-sdk#5549.
function isFirestoreTargetIdError(error) {
  return (
    error?.name === 'FirebaseError' &&
    typeof error?.message === 'string' &&
    error.message.startsWith('Target ID already exists')
  )
}

// The boundary sits above the root ConfigProvider, so the fallback brings its
// own themed provider to follow the user's colour scheme.
function ErrorShell({ children }) {
  const logo = useLogo('bw')
  return (
    <ConfigProvider theme={useAppThemeConfig()}>
      <Layout className={styles.baseLayout}>
        <Layout.Header className={styles.header}>
          <div
            className={styles.topMenu}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <a href="/">
              <img src={logo} alt="cleverlohn" className={styles.logo} />
            </a>
          </div>
        </Layout.Header>
        <Layout.Content className={styles.content}>
          <Row gutter={gutter}>
            <Col xs={24}>{children}</Col>
          </Row>
        </Layout.Content>
      </Layout>
    </ConfigProvider>
  )
}

class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, permissionDenied: false }
  }

  static getDerivedStateFromError(error) {
    const permissionDenied =
      error.name === 'FirebaseError' && error.code === 'permission-denied'

    return { hasError: true, permissionDenied, error }
  }

  componentDidCatch(error) {
    if (error.name === 'TypeError' && isChunkLoadError(error)) {
      const RELOAD_KEY = 'chunk-load-error-reload'
      const lastReload = sessionStorage.getItem(RELOAD_KEY)
      const now = Date.now()

      if (!lastReload || now - Number(lastReload) > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(now))
        window.location.reload()
        return
      }
    }

    if (isFirestoreTargetIdError(error)) {
      Sentry.captureException(error, {
        tags: { autoReload: 'firestore-target-id' },
      })

      const RELOAD_KEY = 'firestore-target-id-reload'
      const lastReload = sessionStorage.getItem(RELOAD_KEY)
      const now = Date.now()

      if (!lastReload || now - Number(lastReload) > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(now))
        window.location.reload()
      }
      return
    }

    if (error.name === 'FirebaseError' && error.code === 'permission-denied') {
      return
    }

    Sentry.captureException(error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorShell>
          {this.state.permissionDenied ? (
            <AccessDeniedComponent auth={this.props.auth} />
          ) : (
            <ErrorContent error={this.state.error} auth={this.props.auth} />
          )}
        </ErrorShell>
      )
    }

    return this.props.children
  }
}

export default GlobalErrorBoundary
